import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { ActivatedRoute } from "@angular/router";
import { ListSelectTypes } from '@app/Constants';
import { ListAliases } from '@app/Constants/ListAliases';
import { combineLatest, Observable, Subscription } from "rxjs";
import { map } from 'rxjs/operators';
import { FormFlowFieldTypes } from '../../../../../Constants/FormFlowFieldTypes';
import { ValidationRegexPatterns } from '../../../../../Constants/ValidationRegexPatterns';
import { ListViewModel } from '../../../../../Models/ListViewModel';
import { SimpleFormFlowHierarchyAttribute } from "../../../../../Models/SimpleFormFlowHierarchyAttribute";
import { TenantService } from "../../../../../Services";
import { FormFlowService } from "../../../../../Services/formFlow.service";
import { LocationAttribute } from "../../Models/location-attribute.model";
import { LocationAttributesService } from '../../Services/location-attributes.service';
import { ModuleService } from "../../Services/module.service";
import { MatToolbar } from '@angular/material/toolbar';
import { MatError, MatFormField, MatLabel, MatInput } from '@angular/material/input';
import { MatSelect } from '@angular/material/select';
import { FormsModule } from '@angular/forms';
import { MatOption } from '@angular/material/autocomplete';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';

@Component({
    selector: 'aw-location-attribute-editor',
    templateUrl: './location-attribute-editor.component.html',
    styleUrls: ['./location-attribute-editor.component.scss'],
    imports: [MatToolbar, MatFormField, MatLabel, MatSelect, FormsModule, MatOption, MatInput, MatCheckbox, MatButton, MatIcon, MatError]
})
export class LocationAttributeEditorComponent implements OnInit, OnDestroy {
    @Input('field') field: Observable<{ attribute: LocationAttribute }>;
    @Input('title') sidebarTitle: string;
    @Output('closePanel') closePanel: EventEmitter<any> = new EventEmitter();

    addFieldSideBarGroupTitle: string = "";
    editMode: boolean = false;
    fieldForm: LocationAttribute = new LocationAttribute();

    parentFields: SimpleFormFlowHierarchyAttribute[];
    parentField: SimpleFormFlowHierarchyAttribute;

    calculationTypes: any[];
    calculationType: number;

    drivingAttributes: any[];
    drivingAttribute: string;

    drivingField: LocationAttribute;
    drivingFields: LocationAttribute[] = [];

    isDisabled: boolean = false;

    isArchived: boolean = true;
    fieldTypes: string[] = [];
    listType: string;

    listValueType: "AIRSWEBLIST";
    lists: ListViewModel[];
    listValues: any[];

    listAlias: string;

    get list(): ListViewModel {
        return this.lists.find(list => list.Key === this.listAlias);
    }

    readonly defaultListSelectTypes: ListSelectTypes[] = [
        ListSelectTypes.SingleDropDown,
        ListSelectTypes.SingleRadios,
        ListSelectTypes.MultiDropDown,
        ListSelectTypes.MultiCheckbox,
        ListSelectTypes.JumboRadio
    ];
    isOfTypes: ListSelectTypes[];
    isOfType: string;

    moduleId: string = "";
    version: number = 1;
    stepId: string = "";

    selectedValue: string;

    listItemsCache: any = {};

    errorString: string;
    fieldTypeError: string;
    relatedToCalcField: boolean;

    unsignedIntegerInputRestriction = ValidationRegexPatterns.UnsignedIntegerInputRestriction;
    signedIntegerInputRestriction = ValidationRegexPatterns.SignedIntegerInputRestriction;
    unsignedDecimalInputRestriction = ValidationRegexPatterns.UnsignedDecimalInputRestriction;
    signedDecimalInputRestriction = ValidationRegexPatterns.SignedDecimalInputRestriction;

    private subscriptions: Subscription[] = [];

    constructor(private tenantService: TenantService,
        private moduleService: ModuleService,
        private formFlowService: FormFlowService,
        private activatedRoute: ActivatedRoute,
        private locationAttributesService: LocationAttributesService) { }

    ngOnInit() {
        this.isOfTypes = [...this.defaultListSelectTypes];
        this.moduleId = this.activatedRoute.snapshot.params["moduleId"];
        this.version = this.activatedRoute.snapshot.params["version"];
        this.sidebarTitle = "Add Field";
        this.locationAttributesService.getAllowedFieldTypes().then((allowedFieldTypes) => { this.fieldTypes = allowedFieldTypes });

        this.subscriptions.push(
            this.tenantService.getAddedList$().subscribe(listAlias => this.listAlias = listAlias)
        );

        this.subscriptions.push(
            combineLatest(
                this.locationAttributesService.getListsUpdated(),
                this.activatedRoute.queryParams
            )
                .subscribe(([params]) => {
                    this.tenantService.getLists()
                        .subscribe(r => {
                            this.lists = r;
                        });

                    this.formFlowService.getCalculationTypes()
                        .subscribe(res => {
                            this.calculationTypes = res;
                        });
                })
        );

        this.subscriptions.push(
            this.field.subscribe(field => {
                if (field.attribute) {
                    this.fieldForm = Object.assign({}, field.attribute);
                    this.fieldForm.OriginalFieldId = this.fieldForm.AttributeId;
                    this.editMode = true;
                    this.sidebarTitle = "Edit Field";
                    this.selectedValue = this.fieldForm.AttributeType;
                    this.listAlias = this.fieldForm.ListAlias;
                    this.listType = this.fieldForm.ListType;
                    this.listValueType = this.fieldForm.ListValueType;
                    this.isOfType = this.fieldForm.IsOfType;
                    this.calculationType = this.fieldForm.CalculationType;
                    this.parentField = this.fieldForm.DrivingField;
                    this.drivingAttribute = this.fieldForm.DrivingAttribute;
                    this.isDisabled = this.fieldForm.IsDeactivated;
                    this.fieldForm.index = this.locationAttributesService.attributes.indexOf(field.attribute);
                } else {
                    this.fieldForm = new LocationAttribute();
                    this.selectedValue = this.fieldForm.AttributeType;
                    this.editMode = false;
                    this.sidebarTitle = "Add Field";
                    this.listAlias = null;
                    this.listType = null;
                    this.isOfType = null;
                    this.fieldForm.IsDeactivated = false;
                    this.fieldTypeValidate();
                }
            })
        );
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach(sub => this.unsubscribe(sub));
    }

    onListAliasClick(): void {
        this.isOfTypes = [...this.defaultListSelectTypes];
        if (this.listAlias === ListAliases.GeneralYesNo) {
            if (this.isOfType === ListSelectTypes.MultiDropDown || this.isOfType === ListSelectTypes.MultiCheckbox) {
                this.isOfType = null;
            }
            this.isOfTypes = this.isOfTypes.filter((type) => type != ListSelectTypes.MultiDropDown && type != ListSelectTypes.MultiCheckbox);
        }
    }

    private unsubscribe(subscription: Subscription) {
        if (subscription !== null) {
            subscription.unsubscribe();
        }
    }

    saveField(): void {
        if (!this.isDisabled) {
            this.isDisabled = true;
            this.fieldForm.AttributeId = this.getFieldIdFromFieldNameAndType(
                this.fieldForm
            );
            if (!this.validationPass()) {
                this.isDisabled = false;
                return;
            }

            switch (this.selectedValue) {
                case "list":
                    this.fieldForm.AttributeUniqueId = "list";
                    this.fieldForm.IsOfType = this.isOfType;
                    this.fieldForm.ListType = this.isOfType;
                    this.fieldForm.ListAlias = this.listAlias;
                    break;
                case "text":
                case "number":
                case "email":
                    this.fieldForm.AttributeUniqueId = "textbox";
                    break;
                case "date":
                case "datetime":
                case "datetimepicker":
                    this.fieldForm.AttributeUniqueId = "datetimepicker";
                    break;
                case "checkbox":
                    this.fieldForm.AttributeUniqueId = "checkbox";
                    break;
                case "geolocation":
                    this.fieldForm.AttributeUniqueId = "geolocation";
                    break;

                case "cron":
                    this.fieldForm.AttributeUniqueId = "cron";
                    break;
                case "calc":
                    this.fieldForm.AttributeUniqueId = "calc";
                    this.fieldForm.CalculationType = this.calculationType;
                    this.fieldForm.DrivingField = this.parentField;
                    this.fieldForm.DrivingAttribute = this.drivingAttribute;
                    break;
            }
            this.fieldForm.AttributeType = this.selectedValue;
            this.fieldForm.GroupName = this.addFieldSideBarGroupTitle;
            this.fieldForm.IsCustomField = true;

            this.locationAttributesService
                .addAttribute(
                    this.fieldForm,
                    this.tenantService.selectedTenant
                )
                .subscribe({
                    next: () => this.closePanel.emit(),
                    error: (err) => this.handleSaveError(err)
                });
        }
    }

    addList() {
        this.moduleService.addList();
    }

    fieldRulesEditable(field: LocationAttribute): boolean {
        return (
            field.AttributeId !== this.drivingField.AttributeId &&
            !field.IsMandatory &&
            !field.IsAlwaysHidden
        );
    }

    getFieldIdFromFieldNameAndType(field: LocationAttribute): string {
        let fieldId = field.AttributeId;

        if (fieldId.endsWith("Aliases")) {
            const idx = fieldId.lastIndexOf("Aliases");
            fieldId = fieldId.substring(0, idx);
        } else if (fieldId.endsWith("Alias")) {
            const idx = fieldId.lastIndexOf("Alias");
            fieldId = fieldId.substring(0, idx);
        }

        if (this.selectedValue === "list") {
            switch (this.isOfType) {
                case ListSelectTypes.SingleDropDown:
                case ListSelectTypes.SingleRadios:
                case ListSelectTypes.JumboRadio:
                    fieldId += "Alias";
                    break;
                case ListSelectTypes.MultiDropDown:
                case ListSelectTypes.MultiCheckbox:
                    fieldId += "Aliases";
                    break;
            }
        }
        return this.slugify(fieldId);
    }

    getDrivingAttributes(parentField: SimpleFormFlowHierarchyAttribute): void {
        if (parentField) {
            const field = this.moduleService.getField(
                parentField.StepId,
                parentField.ParentPropertyName
            );

            this.tenantService
                .getListAttributeDefinitions(field.ListAlias)
                .subscribe(res => {
                    this.drivingAttributes = res;
                });
        }
    }

    getListItems() {
        const cacheVal = this.listItemsCache[
            this.drivingField.ListParams["ListAlias"]
        ];
        let itemsSource: Observable<any[]>;
        if (cacheVal) {
            itemsSource = new Observable<any>((items: any) => items.next(cacheVal));
        } else {
            itemsSource = this.tenantService
                .getListItems([
                    "AIRSWEBLIST",
                    this.drivingField.ListParams["ListAlias"]
                ]).pipe(map(r => {
                    return r as any[];
                }))
        }
        return itemsSource.pipe(map(r => {
            for (let i of r) {
                i.shown = true;
            }
            return r;
        }), map((items: any) => {
            this.listValues = items;
            this.listItemsCache[this.drivingField.ListParams["ListAlias"]] = items;
        }))
    }

    slugify(input: string): string {
        if (!input) return null;

        // trim and remove invalid chars
        return input.trim().replace(/[^a-zA-Z0-9_]/g, "");
    }

    onFieldTypeChanged(): boolean {
        if (this.fieldTypeValidate()) {
            if (this.selectedValue === FormFlowFieldTypes.Number) {
                this.fieldForm.RegexRestrict = this.unsignedIntegerInputRestriction;
            }
            else {
                this.fieldForm.RegexRestrict = null;
            }

            return true;
        }

        return false;
    }

    fieldTypeValidate() {
        if (!this.selectedValue) {
            this.fieldTypeError = "Please select a field type.";
            return false;
        } else if (
            this.fieldForm.AttributeType === "list" &&
            this.selectedValue !== "list" &&
            this.relatedToCalcField
        ) {
            this.fieldTypeError = "Field type is related to a calc field.";
            return false;
        } else {
            this.fieldTypeError = null;
            return true;
        }
    }

    updateField(): void {
        this.locationAttributesService
            .updateAttribute(
                this.fieldForm,
                this.tenantService.selectedTenant
            )
            .subscribe({
                next: () => this.closePanel.emit(),
                error: (err) => this.handleSaveError(err)
            });
    }

    handleSaveError(error: any): void {
        this.isDisabled = false;
        if (error?.status === 409) {
            this.errorString = error?.error?.Message ?? error?.error?.message ?? "Alias is already used elsewhere.";
        } else {
            this.errorString = "Failed to save: an unexpected error occurred.";
            console.error(error);
        }
    }

    compareSimpleFieldViewModels(
        o1: SimpleFormFlowHierarchyAttribute,
        o2: SimpleFormFlowHierarchyAttribute
    ): boolean {
        if (
            o1 &&
            o2 &&
            o1.StepId === o2.StepId &&
            o1.ParentPropertyName === o2.ParentPropertyName
        ) {
            return true;
        }

        return false;
    }

    onUpdateFieldClick(): void {
        let updatedFieldUniqueId: string;

        switch (this.selectedValue) {
            case "list":
                updatedFieldUniqueId = "list";
                break;
            case "text":
            case "number":
            case "email":
                updatedFieldUniqueId = "textbox";
                break;
            case "date":
            case "datetime":
            case "datetimepicker":
                updatedFieldUniqueId = "datetimepicker";
                break;
            case "checkbox":
                updatedFieldUniqueId = "checkbox";
                break;
            case "geolocation":
                updatedFieldUniqueId = "geolocation";
                break;
            case "cron":
                updatedFieldUniqueId = "cron";
                break;
            case "calc":
                updatedFieldUniqueId = "calc";
                break;
        }

        if (this.selectedValue === "list") {
            this.fieldForm.IsOfType = this.isOfType;
            this.fieldForm.ListType = this.fieldForm.IsOfType;
            this.fieldForm.ListValueType = this.listValueType;
            this.fieldForm.ListAlias = this.listAlias;
        }

        this.updateField();
    }

    relatedToCalculationField(fieldId: string) {
        this.formFlowService
            .IsFieldLinkedAnotherOfType(
                fieldId,
                this.tenantService.selectedTenant.TenantId,
                this.moduleId,
                this.version,
                "calc"
            )
            .subscribe(result => {
                if (result !== undefined) {
                    this.relatedToCalcField = result;
                } else {
                    this.relatedToCalcField = false;
                }
            });
    }

    disableManageListItems(): boolean {
        return !this.list;
    }

    manageListItems() {
        this.moduleService.editList(this.list.Key, this.listAlias)
    }

    validationPass(): boolean {
        if (this.fieldForm.AttributeId && this.fieldForm.AttributeName && this.fieldForm.HelpText) {
            return (
                !this.errorString &&
                !this.fieldTypeError &&
                this.numberMandatoryFieldsValidation() &&
                this.calculationManatoryFieldsValidation() &&
                this.listMandatoryFieldsValidation()
            );
        }

        return false;
    }

    numberMandatoryFieldsValidation() {
        return this.selectedValue !== FormFlowFieldTypes.Number || this.fieldForm.RegexRestrict;
    }

    listMandatoryFieldsValidation(): boolean {
        if (this.selectedValue !== "list") {
            return true;
        }
        return !!this.isOfType && !!this.listAlias;
    }

    calculationManatoryFieldsValidation() {
        if (
            this.selectedValue === "calc" &&
            (!this.calculationType || !this.drivingAttribute || !this.parentField)
        ) {
            return false;
        }

        return true;
    }

    validate(fieldName: string, fieldValue: string) {
        if (fieldName === "Field Id") {
            if (!fieldValue) {
                this.errorString = "Field Id cannot be blank.";
                return false;
            }
            let fieldValueUpper = fieldValue.toUpperCase();
            let compareLetter = new RegExp("^[0-9]");
            let matchNum = fieldValue.match(compareLetter);
            if (matchNum && matchNum.length >= 0) {
                this.errorString = "Field Id cannot start with a number.";
                return false;
            }
            if (fieldValueUpper.startsWith("XML")) {
                this.errorString = "Field Id cannot start with xml.";
                return false;
            }
            const originalIdUpper = this.fieldForm.OriginalFieldId?.toUpperCase();
            const collidingHa = this.locationAttributesService.attributes.find(a =>
                (this.editMode ? a.AttributeId?.toUpperCase() !== originalIdUpper : true) &&
                (a.AttributeId?.toUpperCase() === fieldValueUpper ||
                 a.AttributeName?.toUpperCase() === fieldValueUpper)
            );
            if (collidingHa) {
                this.errorString =
                    "Field Id cannot match another Hierarchy Attribute's Id or Name.";
                return false;
            }
            if (!fieldValue || fieldValue.length <= 0) {
                this.errorString = "Field Id cannot be blank.";
                return false;
            }
            const regex = new RegExp("^[a-zA-Z0-9_]*$");
            if (!regex.test(fieldValue)) {
                this.errorString =
                    "Field Id may only contain alpha-numeric characters and underscores";
                return false;
            }
        } else if (fieldName === "Label") {
            if (!fieldValue || fieldValue.length <= 0) {
                this.errorString = "Label cannot be blank.";
                return false;
            }
        } else if (fieldName === "calc") {
        }

        this.errorString = null;
        this.isDisabled = false;
        return true;
    }
}
